// DashboardPOC.js — Documentation Fonctionnelle : POC « Tableau de bord ScanBank » (module Rapports & Supervision).
// Aperçu interactif de l'écran réel d'accueil, fidèle à sa DISPOSITION :
//   - bandeau héro (titre, bascule auto-refresh, bouton Actualiser) ;
//   - rangée KPI : jauge « Conformité » (score moyen global) + 4 tuiles (Sessions d'Audit,
//     Anomalies Ouvertes, Recommandations, Domaines Actifs) ;
//   - section « Mes tâches » (anomalies / recommandations affectées à l'utilisateur courant) ;
//   - ligne 2 : Sessions Récentes + Anomalies Critiques ;
//   - ligne 3 : Recommandations Prioritaires + Domaines d'Audit (barres de score 70/85).
// Graphiques = divs « maison » (pas de lib). Actions SIMULÉES localement (démo, aucun appel réseau).
// Source de vérité : scanbank-frontend/src/features/[SCANBANK] Reports/[AUD][00]-Dashboard.js
// + agrégats serveur GET /api/scanbank/reports/overview (ReportController, SCANBANK_REPORT_VIEW).
import React, { useState } from 'react';
import {
  Shield, Activity, AlertOctagon, AlertTriangle, AlertCircle, Lightbulb, Server,
  RefreshCw, ArrowRight, Target, CheckCircle, XCircle, Clock, UserCheck,
  Zap, Info, BarChart3, ListChecks, Gauge, Calculator,
} from 'lucide-react';
import { Card, CardContent, Badge } from '../ui';

// ---- Seuils & couleurs de score (règle ScanBank : ACCEPTABLE ≥ 85, ALERTE ≥ 70, CRITIQUE sinon)
const scoreColor = (s) => (s == null ? '#9CA3AF' : s >= 85 ? '#10B981' : s >= 70 ? '#F59E0B' : '#EF4444');
const scoreLevel = (s) => (s == null ? '—' : s >= 85 ? 'ACCEPTABLE' : s >= 70 ? 'ALERTE' : 'CRITIQUE');

// ---- Dictionnaires d'énumérations (valeurs backend EXACTES + libellés FR) -----
const SESSION_STATUTS = {
  PENDING: { label: 'En attente', color: '#F59E0B', icon: Clock },
  IN_PROGRESS: { label: 'En cours', color: '#3B82F6', icon: RefreshCw },
  COMPLETED: { label: 'Terminée', color: '#10B981', icon: CheckCircle },
  FAILED: { label: 'Échouée', color: '#EF4444', icon: XCircle },
};

const SEVERITES = {
  CRITICAL: { label: 'Critique', color: '#EF4444', icon: AlertOctagon },
  MAJOR: { label: 'Majeure', color: '#F97316', icon: AlertTriangle },
  MINOR: { label: 'Mineure', color: '#F59E0B', icon: AlertCircle },
};

const ANOMALIE_STATUTS = {
  OPEN: { label: 'Ouverte', color: '#EF4444' },
  ACKNOWLEDGED: { label: 'Prise en compte', color: '#3B82F6' },
  ESCALATED: { label: 'Escaladée', color: '#F97316' },
  RESOLVED: { label: 'Résolue', color: '#10B981' },
  IGNORED: { label: 'Ignorée', color: '#6B7280' },
};

const RECO_PRIORITES = {
  CRITICAL: { label: 'Critique', color: '#EF4444' },
  HIGH: { label: 'Haute', color: '#F97316' },
  MEDIUM: { label: 'Moyenne', color: '#F59E0B' },
  LOW: { label: 'Basse', color: '#6B7280' },
};

const RECO_STATUTS = {
  PENDING: { label: 'En attente', color: '#F59E0B' },
  ACCEPTED: { label: 'Acceptée', color: '#3B82F6' },
  IN_PROGRESS: { label: 'En cours', color: '#8B5CF6' },
  IMPLEMENTED: { label: 'Implémentée', color: '#10B981' },
  REJECTED: { label: 'Rejetée', color: '#EF4444' },
};

// ---- Données en dur : réponse simulée de GET /api/scanbank/reports/overview ----
// KPI globaux + agrégats par domaine, calculés côté serveur (aucune table intégrale
// téléchargée par l'écran réel — optimisation OPT-8).
const OVERVIEW = {
  sessions: { total: 50, byStatus: { PENDING: 3, IN_PROGRESS: 2, COMPLETED: 42, FAILED: 3 }, aujourdhui: 2, scoreMoyen: 81.0 },
  anomalies: { ouvertes: 41, critiquesOuvertes: 9 },
  recommandations: { enAttente: 17 },
  domaines: [
    { code: 'CBS_AMPLITUDE', name: 'Core Banking Amplitude', totalSessions: 36, scoreMoyen: 81.7, totalAnomalies: 87, isActive: true },
    { code: 'CBS_BANK_CEMAC', name: 'Core Banking CEMAC', totalSessions: 10, scoreMoyen: 84.2, totalAnomalies: 14, isActive: true },
    { code: 'AML_TRACKER', name: 'Anti-Blanchiment Tracker', totalSessions: 4, scoreMoyen: 66.8, totalAnomalies: 13, isActive: true },
  ],
};

// Sessions récentes : page bornée (size=4, sort=id,desc) de GET /api/scanbank/audit/sessions/page
const SESSIONS_RECENTES = [
  { id: 61, sessionCode: 'SES_CBS_AMPLITUDE_VIREMENTS_20260711154210_S7T8U9V0', module: 'VIREMENTS', domaine: 'Core Banking Amplitude', status: 'IN_PROGRESS', conformityScore: null, startTime: '11/07 15:42' },
  { id: 58, sessionCode: 'SES_CBS_AMPLITUDE_CREDITS_20260711081420_J9K0L1M2', module: 'CREDITS', domaine: 'Core Banking Amplitude', status: 'COMPLETED', conformityScore: 77.0, startTime: '11/07 08:14' },
  { id: 55, sessionCode: 'SES_CBS_AMPLITUDE_COMPTABILITE_20260710141005_E5F6G7H8', module: 'COMPTABILITE', domaine: 'Core Banking Amplitude', status: 'COMPLETED', conformityScore: 90.0, startTime: '10/07 14:10' },
  { id: 52, sessionCode: 'SES_CBS_BANK_CEMAC_VIREMENTS_20260709163055_N3P4Q5R6', module: 'VIREMENTS', domaine: 'Core Banking CEMAC', status: 'COMPLETED', conformityScore: 91.3, startTime: '09/07 16:30' },
];

// Anomalies critiques ouvertes : page bornée (status=OPEN, severity=CRITICAL, size=4)
const ANOMALIES_CRITIQUES = [
  { id: 901, anomalyCode: 'ANO_HAB_20260708_001', message: "3 comptes génériques actifs avec profil superviseur", severity: 'CRITICAL', status: 'OPEN' },
  { id: 902, anomalyCode: 'ANO_HAB_20260708_002', message: "12 habilitations sensibles sans revue depuis plus de 12 mois", severity: 'CRITICAL', status: 'ESCALATED' },
  { id: 903, anomalyCode: 'ANO_CRD_20260711_001', message: "Dépassement du seuil réglementaire sur 5 dossiers de crédit sans dérogation", severity: 'CRITICAL', status: 'OPEN' },
  { id: 904, anomalyCode: 'ANO_AML_20260710_001', message: "Client sous sanction non filtré lors de l'entrée en relation", severity: 'CRITICAL', status: 'ACKNOWLEDGED' },
];

// Recommandations en attente : page bornée (status=PENDING, size=3)
const RECOMMANDATIONS_PRIORITAIRES = [
  { id: 71, title: 'Désactiver les comptes génériques à privilèges', suggestedOwner: 'RSSI', dueDate: '15/07/2026', priority: 'CRITICAL', status: 'PENDING' },
  { id: 72, title: 'Régulariser les dérogations sur les dossiers crédit hors seuil', suggestedOwner: 'Direction Crédits', dueDate: '18/07/2026', priority: 'CRITICAL', status: 'PENDING' },
  { id: 73, title: 'Mettre en place la revue annuelle des habilitations sensibles', suggestedOwner: 'Resp. Habilitations', dueDate: '23/07/2026', priority: 'HIGH', status: 'PENDING' },
];

// « Mes tâches » : compteurs dérivés de GET /api/scanbank/anomalies/stats?assignedTo={moi}
// et GET /api/scanbank/recommendations/stats?assignedTo={moi} (statuts NON finaux uniquement)
const MES_TACHES = { anomaliesOuvertes: 3, recommandationsEnCours: 2 };

// ---- Sous-composants « maison » -----------------------------------------------
// Jauge circulaire de conformité (conic-gradient, pas de SVG/lib).
const ScoreCircle = ({ score, size = 84 }) => {
  const color = scoreColor(score);
  const value = score == null ? 0 : Math.round(score);
  return (
    <div className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <div className="rounded-full" style={{ width: size, height: size, background: `conic-gradient(${color} ${value * 3.6}deg, rgba(156,163,175,0.25) 0deg)` }} />
      <div className="absolute rounded-full bg-white dark:bg-gray-800 flex flex-col items-center justify-center" style={{ width: size - 16, height: size - 16 }}>
        <span className="text-lg font-bold leading-none" style={{ color }}>{score == null ? '—' : `${value}%`}</span>
        <span className="text-[9px] font-semibold mt-0.5" style={{ color }}>{scoreLevel(score)}</span>
      </div>
    </div>
  );
};

const KpiCard = ({ icon: Ic, label, value, subtitle, color, onClick }) => (
  <Card className={`p-4 h-full ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={onClick}>
    <div className="flex items-center justify-between h-full">
      <div className="min-w-0">
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">{label}</p>
        {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{subtitle}</p>}
      </div>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1A`, color }}>
        <Ic className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

const SevBadge = ({ severity }) => {
  const c = SEVERITES[severity] || SEVERITES.MINOR;
  const Ic = c.icon;
  return <Badge color={c.color}><Ic className="h-3 w-3" />{c.label}</Badge>;
};

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page ----------------------------------------------------------------------
const DashboardPOC = () => {
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [toast, setToast] = useState('');

  const notify = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 2200); };

  const s = OVERVIEW.sessions;
  const domaines = [...OVERVIEW.domaines].sort((a, b) => (a.scoreMoyen ?? 999) - (b.scoreMoyen ?? 999));

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tableau de bord ScanBank</h1>
          <Badge color="#10B981">Module Rapports &amp; Supervision</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Tableau de Bord</strong> — page d'accueil de la plateforme
          (démo, actions simulées localement, données de l'agrégat serveur <code className="text-xs">reports/overview</code>).
        </p>
      </div>

      {/* ===== (2) Aperçu interactif ===== */}
      <Card className="overflow-hidden">
        {/* En-tête héro type app */}
        <div className="px-5 py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-xl bg-white/20 shrink-0"><Shield className="h-6 w-6" /></div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight truncate">ScanBank — Tableau de Bord</h2>
              <p className="text-xs text-white/80 truncate">Centralisation et analyse des résultats d'audit CBS</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={() => { setAutoRefresh(!autoRefresh); notify(autoRefresh ? 'Auto-refresh désactivé (simulation).' : 'Auto-refresh activé : rafraîchissement toutes les 60 s, en pause si onglet masqué (simulation).'); }}
              className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors">
              <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-emerald-400 animate-pulse' : 'bg-white/40'}`} />
              <span className="hidden sm:inline">Auto-refresh</span>
            </button>
            <button onClick={() => notify('Données actualisées depuis reports/overview (simulation).')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors">
              <RefreshCw className="h-3.5 w-3.5" />Actualiser
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* KPIs : jauge Conformité + 4 tuiles */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card className="p-3 col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 h-full">
                <ScoreCircle score={s.scoreMoyen} size={76} />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Conformité</p>
                  <div className="mt-1.5 space-y-1 text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />{s.byStatus.COMPLETED} terminées</div>
                    <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse shrink-0" />{s.byStatus.IN_PROGRESS} en cours</div>
                    <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />{s.byStatus.FAILED} échecs</div>
                  </div>
                </div>
              </div>
            </Card>
            <KpiCard icon={Activity} label="Sessions d'Audit" value={s.total} subtitle={`${s.byStatus.IN_PROGRESS} en cours • ${s.aujourdhui} aujourd'hui`} color="#3B82F6"
              onClick={() => notify('Navigation vers Suivi Audit > Sessions (simulation).')} />
            <KpiCard icon={AlertOctagon} label="Anomalies Ouvertes" value={OVERVIEW.anomalies.ouvertes} subtitle={`${OVERVIEW.anomalies.critiquesOuvertes} critiques ouvertes`} color="#EF4444"
              onClick={() => notify('Navigation vers Suivi Audit > Anomalies (simulation).')} />
            <KpiCard icon={Lightbulb} label="Recommandations" value={OVERVIEW.recommandations.enAttente} subtitle="en attente" color="#F59E0B"
              onClick={() => notify('Navigation vers Suivi Audit > Recommandations (simulation).')} />
            <KpiCard icon={Server} label="Domaines Actifs" value={OVERVIEW.domaines.length} subtitle="Systèmes CBS surveillés" color="#8B5CF6"
              onClick={() => notify('Navigation vers Paramétrage > Domaines (simulation).')} />
          </div>

          {/* Mes tâches (affectation) */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
              <UserCheck className="h-4 w-4 text-indigo-500" />Mes tâches
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => notify('Navigation vers Anomalies filtrées « À moi » (?mine=true) — simulation.')}
                className="flex items-center justify-between p-3 rounded-xl bg-red-50/80 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-left">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 shrink-0"><AlertOctagon className="h-4 w-4 text-red-500" /></div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white leading-none">{MES_TACHES.anomaliesOuvertes}</p>
                    <p className="text-xs text-gray-500 mt-1 truncate">Mes anomalies ouvertes</p>
                  </div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              </button>
              <button onClick={() => notify('Navigation vers Recommandations filtrées « À moi » (?mine=true) — simulation.')}
                className="flex items-center justify-between p-3 rounded-xl bg-amber-50/80 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors text-left">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 shrink-0"><Lightbulb className="h-4 w-4 text-amber-500" /></div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white leading-none">{MES_TACHES.recommandationsEnCours}</p>
                    <p className="text-xs text-gray-500 mt-1 truncate">Mes recommandations en cours</p>
                  </div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              </button>
            </div>
          </div>

          {/* Ligne 2 : Sessions Récentes + Anomalies Critiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Activity className="h-4 w-4 text-blue-500" />Sessions Récentes
                </h3>
                <button onClick={() => notify('Navigation vers la liste complète des sessions (simulation).')}
                  className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium">Voir tout <ArrowRight className="h-3.5 w-3.5" /></button>
              </div>
              <div className="space-y-1.5">
                {SESSIONS_RECENTES.map((sess) => {
                  const st = SESSION_STATUTS[sess.status] || SESSION_STATUTS.PENDING;
                  return (
                    <div key={sess.id} onClick={() => notify(`Ouverture du détail de la session ${sess.sessionCode} (simulation).`)}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50/80 dark:bg-gray-700/40 hover:bg-gray-100 dark:hover:bg-gray-700/70 cursor-pointer transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="font-mono font-medium text-xs text-gray-900 dark:text-white truncate">{sess.sessionCode}</p>
                        <p className="text-xs text-gray-500 truncate">{sess.module} • {sess.domaine}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3 shrink-0">
                        {sess.conformityScore != null && (
                          <span className="text-sm font-bold" style={{ color: scoreColor(sess.conformityScore) }}>{sess.conformityScore}%</span>
                        )}
                        <Badge color={st.color}>{st.label}</Badge>
                        <span className="text-xs text-gray-400 hidden xl:inline">{sess.startTime}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-red-600 flex items-center gap-2">
                  <Zap className="h-4 w-4" />Anomalies Critiques
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1" title="Anomalies critiques ouvertes"><span className="w-2 h-2 rounded-full bg-red-500" />{OVERVIEW.anomalies.critiquesOuvertes} crit.</span>
                    <span className="flex items-center gap-1" title="Anomalies ouvertes (toutes sévérités)"><span className="w-2 h-2 rounded-full bg-orange-500" />{OVERVIEW.anomalies.ouvertes} ouv.</span>
                  </div>
                  <button onClick={() => notify('Navigation vers la liste complète des anomalies (simulation).')}
                    className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium">Voir tout <ArrowRight className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="space-y-1.5">
                {ANOMALIES_CRITIQUES.map((a) => {
                  const st = ANOMALIE_STATUTS[a.status] || ANOMALIE_STATUTS.OPEN;
                  return (
                    <div key={a.id} onClick={() => notify(`Ouverture du détail de l'anomalie ${a.anomalyCode} (simulation).`)}
                      className="p-2.5 rounded-xl border-l-4 border-red-500 bg-red-50/70 dark:bg-red-900/15 cursor-pointer hover:shadow-sm transition-shadow">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs font-medium text-gray-900 dark:text-white">{a.anomalyCode}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">{a.message}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <SevBadge severity={a.severity} />
                          <Badge color={st.color}>{st.label}</Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Ligne 3 : Recommandations Prioritaires + Domaines d'Audit */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />Recommandations Prioritaires
                </h3>
                <button onClick={() => notify('Navigation vers la liste complète des recommandations (simulation).')}
                  className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium">Voir tout <ArrowRight className="h-3.5 w-3.5" /></button>
              </div>
              <div className="space-y-1.5">
                {RECOMMANDATIONS_PRIORITAIRES.map((r) => {
                  const pr = RECO_PRIORITES[r.priority] || RECO_PRIORITES.LOW;
                  const st = RECO_STATUTS[r.status] || RECO_STATUTS.PENDING;
                  return (
                    <div key={r.id} onClick={() => notify(`Ouverture du détail de la recommandation « ${r.title} » (simulation).`)}
                      className="p-2.5 rounded-xl bg-gray-50/80 dark:bg-gray-700/40 hover:bg-gray-100 dark:hover:bg-gray-700/70 cursor-pointer transition-colors">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{r.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{r.suggestedOwner} • échéance {r.dueDate}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge color={pr.color}>{pr.label}</Badge>
                          <Badge color={st.color}>{st.label}</Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/60 flex justify-between items-center">
                <span className="text-xs text-gray-500">Recommandations en attente (agrégat serveur)</span>
                <span className="text-xs font-bold text-gray-900 dark:text-white">{OVERVIEW.recommandations.enAttente}</span>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Target className="h-4 w-4 text-purple-500" />Domaines d'Audit
                </h3>
                <button onClick={() => notify('Navigation vers Paramétrage > Domaines (simulation).')}
                  className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium">Gérer <ArrowRight className="h-3.5 w-3.5" /></button>
              </div>
              <div className="space-y-2">
                {domaines.map((d) => (
                  <div key={d.code} onClick={() => notify(`Ouverture du tableau de bord du domaine ${d.code} (simulation).`)}
                    className="p-2.5 rounded-xl bg-gray-50/80 dark:bg-gray-700/40 hover:bg-gray-100 dark:hover:bg-gray-700/70 cursor-pointer transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded-lg shrink-0 ${d.isActive ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                        <Server className={`h-3.5 w-3.5 ${d.isActive ? 'text-emerald-500' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{d.name} <span className="text-xs text-gray-400 font-mono">({d.code})</span></p>
                          <span className="text-sm font-bold ml-2 shrink-0" style={{ color: scoreColor(d.scoreMoyen) }}>{d.scoreMoyen}%</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full transition-all duration-700 ease-out" style={{ width: `${d.scoreMoyen || 0}%`, backgroundColor: scoreColor(d.scoreMoyen) }} />
                          </div>
                          <span className="text-xs text-gray-400 shrink-0">{d.totalSessions} sess. • {d.totalAnomalies} ano.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                <Info className="h-3 w-3" />Domaines triés du score le plus faible au plus élevé (priorisation du risque).
              </p>
            </div>
          </div>
        </div>

        {/* Toast simulé */}
        {toast && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Info className="h-4 w-4 text-indigo-500" />{toast}
          </div>
        )}
      </Card>

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Gauge className="h-5 w-5 text-blue-500" />1. Lire les indicateurs</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>La jauge <strong>Conformité</strong> affiche le score moyen global des sessions terminées, coloré par seuils (vert ≥ 85, ambre ≥ 70, rouge sinon).</GuideStep>
              <GuideStep n={2}>Les 4 tuiles KPI donnent les volumes : <strong>sessions</strong>, <strong>anomalies ouvertes</strong> (dont critiques), <strong>recommandations en attente</strong>, <strong>domaines actifs</strong>.</GuideStep>
              <GuideStep n={3}>Chaque tuile est <strong>cliquable</strong> et ouvre l'écran de gestion correspondant.</GuideStep>
            </ol>
          </CardContent></Card>
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><UserCheck className="h-5 w-5 text-blue-500" />2. Traiter mes tâches</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>La section <strong>Mes tâches</strong> compte les anomalies (OPEN / ACKNOWLEDGED / ESCALATED) et recommandations (PENDING / ACCEPTED / IN_PROGRESS) <strong>qui me sont affectées</strong>.</GuideStep>
              <GuideStep n={2}>Un clic ouvre la liste pré-filtrée « À moi » (<code className="text-xs">?mine=true</code>).</GuideStep>
              <GuideStep n={3}>Les <strong>Anomalies Critiques</strong> ouvertes sont à traiter en priorité (workflow acknowledge / resolve / escalate).</GuideStep>
            </ol>
          </CardContent></Card>
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><BarChart3 className="h-5 w-5 text-blue-500" />3. Naviguer par domaine</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>La carte <strong>Domaines d'Audit</strong> liste chaque domaine avec son score moyen, son nombre de sessions et d'anomalies.</GuideStep>
              <GuideStep n={2}>Le tri place le domaine <strong>le plus à risque en premier</strong> (score le plus faible).</GuideStep>
              <GuideStep n={3}>Un clic ouvre le <strong>Tableau de Bord Domaine</strong> ; le bouton <strong>Actualiser</strong> recharge les agrégats. <button onClick={() => notify('Données actualisées (simulation).')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Règles de gestion ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Calculator className="h-5 w-5 text-blue-500" />Règles de gestion — score de conformité</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
            <p className="text-sm font-mono text-gray-800 dark:text-gray-100">Score = 100 − (CRITICAL×10 + MAJOR×5 + MINOR×1) ÷ NbContrôles × 10</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Calculé par session à l'étape SCORING du workflow SCANBANK_AUDIT_SESSION, borné à [0, 100],
              persisté dans <code>audit_session.conformity_score</code>. Le score d'un domaine (et le score global)
              est la <strong>moyenne des scores des sessions terminées</strong>, calculée côté serveur.
            </p>
          </div>
          <div className="space-y-2">
            {[
              ['CRITIQUE', '#EF4444', 'score < 70 — non-conformité majeure, plan d\'action immédiat'],
              ['ALERTE', '#F59E0B', '70 ≤ score < 85 — vigilance, correction planifiée'],
              ['ACCEPTABLE', '#10B981', 'score ≥ 85 — situation conforme, surveillance continue'],
            ].map(([lvl, color, desc]) => (
              <div key={lvl} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                <Badge color={color}>{lvl}</Badge>
                <span className="text-xs text-gray-600 dark:text-gray-300">{desc}</span>
              </div>
            ))}
          </div>
        </div>
        <ul className="mt-4 space-y-1.5 text-sm text-gray-600 dark:text-gray-300 list-disc list-inside">
          <li>Tous les compteurs du tableau de bord sont des <strong>agrégats serveur</strong> (aucune table intégrale téléchargée) ; en cas d'échec partiel d'un appel, la tuile concernée reste à zéro sans bloquer la page.</li>
          <li>« Mes tâches » ne compte que les <strong>statuts non finaux</strong> : anomalies OPEN / ACKNOWLEDGED / ESCALATED, recommandations PENDING / ACCEPTED / IN_PROGRESS.</li>
          <li>L'auto-refresh est <strong>désactivé par défaut</strong> (opt-in), intervalle 60 s, en pause quand l'onglet est masqué.</li>
        </ul>
      </CardContent></Card>

      {/* ===== (5) Endpoints ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><ListChecks className="h-5 w-5 text-blue-500" />Endpoints API</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Méthode', 'Endpoint', 'Usage', 'Permission'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['GET', '/api/scanbank/reports/overview', 'KPI globaux + agrégats par domaine', 'SCANBANK_REPORT_VIEW'],
                ['GET', '/api/scanbank/audit/sessions/page?size=4&sort=id,desc', 'Sessions récentes (page bornée)', 'SCANBANK_AUDIT_VIEW'],
                ['GET', '/api/scanbank/anomalies/page?status=OPEN&severity=CRITICAL&size=4', 'Anomalies critiques ouvertes', 'SCANBANK_ANOMALY_VIEW'],
                ['GET', '/api/scanbank/recommendations/page?status=PENDING&size=3', 'Recommandations prioritaires', 'SCANBANK_RECOMMENDATION_VIEW'],
                ['GET', '/api/scanbank/anomalies/stats?assignedTo={moi}', 'Compteur « Mes anomalies »', 'SCANBANK_ANOMALY_VIEW'],
                ['GET', '/api/scanbank/recommendations/stats?assignedTo={moi}', 'Compteur « Mes recommandations »', 'SCANBANK_RECOMMENDATION_VIEW'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2"><Badge color="#10B981">{r[0]}</Badge></td>
                  <td className="px-4 py-2 font-mono text-xs break-all">{r[1]}</td>
                  <td className="px-4 py-2 text-xs">{r[2]}</td>
                  <td className="px-4 py-2 font-mono text-[11px]">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
          <ArrowRight className="h-3 w-3" />Source : écran réel <code>[AUD][00]-Dashboard.js</code> + ReportController (scanbank_core).
        </p>
      </CardContent></Card>
    </div>
  );
};

export default DashboardPOC;
